/* Copy Pack builder — assembles ONE self-contained clipboard payload per
 * build stage:
 * (1) the authoring guide (GET /cm/app-engine/guide, {version, markdown},
 *     cached per session) — ALWAYS embedded,
 * (2) the stage's dependency JSONs as LEAN projections,
 * (3) the current doc when editing an existing item,
 * (4) a request scaffold ending with "MY REQUEST: …".
 * The intelligence lives outside the system; this is the clipboard bridge. */
import {
    bexHttpClient
} from 'shared/api/clients/bex.httpClient';
import {
    appEngineService
} from '../api/appEngine.service';
import type {
    Capability,
    SourceDoc,
    Tenancy
} from '../api/types';

export type PackStage = 'sources' | 'workflows' | 'journeys';

export interface PackDep {
    key: string;
    label: string;
    doc: any;
}

export interface PackCurrentDoc {
    label: string;
    text: string;
}

export const STAGE_LABELS: Record<PackStage, string> = {
    sources: 'Sources',
    workflows: 'Workflows',
    journeys: 'Journeys'
};

// ── (1) Authoring guide — fetched once, cached for the session ──────────────
const GUIDE_CACHE_KEY = 'aps-authoring-guide-v2';

export interface GuideInfo {
    /** Engine version (sha256 prefix of the markdown); '' when the
     * endpoint did not answer — never mark step 0 done on ''. */
    version: string;
    markdown: string;
}

let guidePromise: Promise<GuideInfo> | null = null;

const GUIDE_FALLBACK = '(Authoring guide unavailable — GET /cm/app-engine/guide '
    + 'did not answer. Author against the locked document shapes; the validator '
    + 'gates every save, so an invalid document cannot land.)';

function guideText(data: any): string {
    if (typeof data === 'string') return data;
    const keys = ['guide', 'text', 'content', 'markdown'];
    for (const k of keys) {
        if (typeof data?.[k] === 'string') return data[k];
    }
    return JSON.stringify(data, null, 2);
}

function readGuideCache(): GuideInfo | null {
    try {
        const raw = sessionStorage.getItem(GUIDE_CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.version === 'string'
            && typeof parsed?.markdown === 'string') return parsed;
    } catch { /* corrupt or unavailable cache — fall through to fetch */ }
    return null;
}

/** The guide itself is org-independent, but the proxy route is org-only
 * tenancy (like /apps) — the request MUST carry org_id or it 400s. */
export function fetchGuide(orgId: string): Promise<GuideInfo> {
    const cached = readGuideCache();
    if (cached) return Promise.resolve(cached);
    if (!guidePromise) {
        guidePromise = bexHttpClient.get('/app-engine/guide', {
            params: {
                org_id: orgId
            }
        })
            .then((r) => {
                const info: GuideInfo = {
                    version: typeof r.data?.version === 'string'
                        ? r.data.version : '',
                    markdown: guideText(r.data)
                };
                try {
                    sessionStorage.setItem(GUIDE_CACHE_KEY, JSON.stringify(info));
                } catch { /* quota — in-memory promise still caches */ }
                return info;
            })
            .catch(() => {
                // Do not cache the failure — a later open retries the fetch.
                guidePromise = null;
                return {
                    version: '',
                    markdown: GUIDE_FALLBACK
                };
            });
    }
    return guidePromise;
}

export function sizeEstimate(chars: number): string {
    const tokens = Math.round(chars / 4);
    return `${chars.toLocaleString()} chars · ~${tokens.toLocaleString()} tokens`;
}

// ── (2) Lean dependency projections per stage ────────────────────────────────
/** Capability projection — strips invocation/connections/auth. */
function projectCapability(c: Capability) {
    return {
        id: c.id,
        name: c.name,
        description: c.description,
        operation_type: c.operation_type,
        risk_level: c.risk_level,
        input: c.input,
        output: c.output
    };
}

function projectSource(doc: SourceDoc) {
    return {
        source: {
            id: doc?.source?.id,
            type: doc?.source?.type,
            name: doc?.source?.name,
            description: doc?.source?.description
        },
        capabilities: (Array.isArray(doc?.capabilities) ? doc.capabilities : [])
            .map(projectCapability)
    };
}

/** Workflow contract projection — id/name/description/input/output only. */
function projectWorkflow(doc: any) {
    return {
        id: doc?.id,
        name: doc?.name,
        description: doc?.description,
        input: doc?.input,
        output: doc?.output
    };
}

async function eachDef(
    t: Tenancy,
    type: 'source' | 'workflow',
    project: (doc: any) => any,
    label: (id: string) => string
): Promise<PackDep[]> {
    const items = await appEngineService.listDefs(t, type);
    const deps = await Promise.all(items.map(async (it) => {
        try {
            const doc = await appEngineService.getDef(t, type, it.id);
            return {
                key: `${type}:${it.id}`,
                label: label(it.id),
                doc: project(doc)
            };
        } catch {
            return null;
        }
    }));
    return deps.filter(Boolean) as PackDep[];
}

async function journeysListDep(t: Tenancy): Promise<PackDep> {
    const items = await appEngineService.listDefs(t, 'journey');
    return {
        key: 'journeys-list',
        label: 'Existing journeys (ids + names)',
        doc: items.map((it) => ({
            id: it.id,
            name: it.name
        }))
    };
}

async function workflowsStageDeps(t: Tenancy): Promise<PackDep[]> {
    return eachDef(t, 'source', projectSource,
        (id) => `Source capabilities — ${id}`);
}

async function journeysStageDeps(t: Tenancy): Promise<PackDep[]> {
    const deps = await eachDef(t, 'workflow', projectWorkflow,
        (id) => `Workflow contract — ${id}`);
    try {
        deps.push(await journeysListDep(t));
    } catch { /* list unavailable — pack still useful */ }
    try {
        const apps = await appEngineService.listDefs(t, 'app');
        const appId = apps[0]?.id || t.app_id;
        const doc = await appEngineService.getDef(t, 'app', appId);
        deps.push({
            key: 'app-doc',
            label: `App document — ${appId}`,
            doc
        });
    } catch { /* app doc unavailable */ }
    return deps;
}

/** The Sources stage has NO dependency docs by design — instructions plus
 * the Connect dialog pointer carry the stage. */
export async function loadStageDeps(t: Tenancy, stage: PackStage): Promise<PackDep[]> {
    if (stage === 'sources') return [];
    if (stage === 'workflows') return workflowsStageDeps(t);
    return journeysStageDeps(t);
}

// ── (4) Request scaffold ─────────────────────────────────────────────────────
const SCAFFOLD_INTRO: Record<PackStage, string> = {
    sources: 'Author a SOURCE document (a connected system and its capability '
        + 'catalog) for the BeX app engine. Prefer the Connect dialog in the '
        + 'studio when a live system exists — the ingester emits the draft; use '
        + 'this pack to write or refine the source JSON by hand.',
    workflows: 'Author WORKFLOW document(s) for the BeX app engine. Every step '
        + 'must call one of the capability ids listed in the dependencies above; '
        + '"with" mappings must be well-formed expressions and declared outputs '
        + 'must resolve.',
    journeys: 'Author JOURNEY document(s) for the BeX app engine. Surfaces bind '
        + 'to the workflow contracts above; context sub-paths must match those '
        + 'contracts and producers must come before consumers.'
};

export function buildScaffold(stage: PackStage, hasCurrentDoc: boolean): string {
    const lines = [SCAFFOLD_INTRO[stage]];
    if (hasCurrentDoc) {
        lines.push('A CURRENT DOCUMENT is included above — apply my request as '
            + 'an edit to it and return the full updated document.');
    }
    lines.push('Rule: For NEW documents omit "id" — the system mints it on '
        + 'create. For EDITS keep the id exactly.');
    lines.push('Rule: return ONLY the JSON document(s), no commentary.');
    lines.push('MY REQUEST: <describe what you want>');
    return lines.join('\n');
}

// ── Assembly ─────────────────────────────────────────────────────────────────
function section(title: string, body: string): string {
    return `===== ${title} =====\n${body}`;
}

export function assemblePack(opts: {
    stage: PackStage;
    guide: GuideInfo;
    deps: PackDep[];
    currentDoc?: PackCurrentDoc | null;
    /** Embed the authoring guide section (default true). */
    includeGuide?: boolean;
}): string {
    const parts: string[] = [];
    if (opts.includeGuide !== false) {
        parts.push(section('AUTHORING GUIDE', opts.guide.markdown));
    }
    for (const d of opts.deps) {
        parts.push(section(`DEPENDENCY — ${d.label}`,
            JSON.stringify(d.doc, null, 2)));
    }
    if (opts.currentDoc) {
        parts.push(section(`CURRENT DOCUMENT — ${opts.currentDoc.label}`,
            opts.currentDoc.text));
    }
    parts.push(section('REQUEST', buildScaffold(opts.stage, !!opts.currentDoc)));
    return parts.join('\n\n');
}
